import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import Maps from "../../components/Map";
import clusterService from "../../services/cluster";
import LoadingPage from "../LoadingPage";
import helpers from "../../utils/helpers";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function ClusterDetail() {
  const { id } = useParams();
  const { data, isLoading, isFetched, refetch } = useQuery(
    "current-cluster",
    () => clusterService.getClusterById(Number(id))
  );

  const alert = useAlert();
  const notification = useNotification();

  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({});
  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral>({
      lat: -6.1753917, // Default initial latitude
      lng: 106.8271533, // Default initial longitude
    });

  useEffect(() => {
    if (isFetched) {
      setMarkerPosition({
        lat: get(data, "data.location.x"),
        lng: get(data, "data.location.y"),
      });
    }
  }, [isFetched, data]);

  const deleteNearby = useMutation(
    "delete-nearby",
    clusterService.deleteNearby,
    {
      onSuccess: () => {
        refetch();
        notification.onOpen({
          message: "Nearby was successfully deleted!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        refetch();
        notification.onOpen({
          message:
            error?.response?.data?.message === undefined
              ? "Something went wrong"
              : error?.response?.data?.message,
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );
  const onDelete = (id: number) => {
    alert.onClose();
    deleteNearby.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this nearby?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  return isLoading ? (
    <LoadingPage />
  ) : (
    <MainLayout>
      <Breadcrumb
        label={get(data, "data.name") || ""}
        breadcrumbs={[
          { label: "Cluster", href: `/app/properties/cluster` },
          { label: "Cluster Detail", href: `/app/properties/cluster/${id}` },
        ]}
      />

      <Grid container>
        <Grid item lg={6} xs={12}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Name
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data.data.name}
            </Typography>
          </Stack>

          

          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Area
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data.data.area}
            </Typography>
          </Stack>

          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Location
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              Latitude : {data.data.location.x}
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              Longitude : {data.data.location.y}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Total Nearby
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data.data.totalNearby}
            </Typography>
          </Stack>
        </Grid>
        <Grid item lg={6} md={12} mt={3}>
          <Box
            sx={{
              height: "300px",
              width: "300px",
            }}
          >
            <Maps center={markerPosition} />
          </Box>
        </Grid>
      </Grid>

      <Typography variant="h2" my={3}>
        Nearby List
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            component={RouterLink}
            to={`/app/properties/cluster/${id}/nearby/create`}
          >
            ADD NEARBY
          </Button>
        </Stack>
      </Box>

      <DataGrid
        autoHeight
        loading={isLoading}
        getRowId={(record) => get(record, "id")}
        rows={get(data, "data.nearby", [])}
        rowCount={get(data, "data.totalNearby", 0)}
        page={options.page_number}
        pageSize={pageSize}
        rowsPerPageOptions={[10, 20, 30]}
        pagination
        disableSelectionOnClick
        onPageChange={(newPage) => {
          setOptions((prev: any) => ({ ...prev, page_number: newPage }));
        }}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        components={{
          NoRowsOverlay: CustomNoRowOverlay,
        }}
        columns={[
          {
            field: "name",
            headerName: "Name",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  <Typography>{params.value}</Typography>
                </Stack>
              );
            },
          },
          {
            field: "icon",
            headerName: "Icon",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  <Typography>{params.value}</Typography>
                </Stack>
              );
            },
          },
          {
            field: "map_name",
            headerName: "Map Name",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  <Typography>{params.value}</Typography>
                </Stack>
              );
            },
          },
          {
            field: "distance",
            headerName: "Distance",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  <Typography>
                    {helpers.displayDistance(params.value)}
                  </Typography>
                </Stack>
              );
            },
          },
          {
            field: "actions",
            headerName: "Action",
            sortable: false,
            filterable: false,
            width: 200,
            renderCell: (params) => {
              return (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to={`/app/properties/cluster/nearby/edit/${get(
                      params,
                      "row.id"
                    )}`}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => onClickDelete(params.row.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              );
            },
          },
        ]}
        sx={{
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
        }}
      />
    </MainLayout>
  );
}
